import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, Lock, Clock, ChevronRight, ClipboardList, Award, CheckCircle2, Sparkles, Info, Languages, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COURSE_DETAIL } from "@/constants/testIds";
import { MissedPremiumRecap } from "@/components/MissedPremiumRecap";

const RANK = { free: 0, basic: 1, premium: 2 };

export default function CourseDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [certStatus, setCertStatus] = useState(null);
  const [issuing, setIssuing] = useState(false);

  const signedIn = !!user && typeof user === "object";

  const loadCourse = useCallback(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  useEffect(() => {
    if (!signedIn) { setCertStatus(null); return; }
    api.get(`/certificates/course/${id}/status`)
      .then((r) => setCertStatus(r.data))
      .catch(() => setCertStatus(null));
  }, [id, signedIn]);

  const claimCertificate = useCallback(async () => {
    setIssuing(true);
    try {
      const { data } = await api.post(`/certificates/course/${id}/issue`);
      toast.success(t("courseDetailUi.certificateClaimed"));
      navigate(`/certificates/${data.code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("courseDetailUi.certificateClaimError"));
      setIssuing(false);
    }
  }, [id, navigate, t]);

  if (!course) {
    return <div className="max-w-7xl mx-auto p-16 text-slate-500">{t("courseDetailUi.loading")}</div>;
  }

  const userTier = user?.role === "admin" ? "premium" : (user?.tier || "free");
  const userRank = RANK[userTier] ?? 0;
  const isFreeTier = signedIn && userTier === "free";

  // Collect free-preview lessons for the upsell rail
  const freePreviews = [];
  (course.chapters || []).forEach((ch) => {
    (ch.lessons || []).forEach((l) => {
      if (l.free_preview) freePreviews.push({ ...l, chapterNumber: ch.number });
    });
  });

  return (
    <div data-testid={COURSE_DETAIL.root} className="bg-[#F8FAFC]">
      {/* HERO — banner image stands on its own at full width, max 420px
          tall so wide landscape covers display beautifully without
          dominating the viewport. Title + meta sit in a clean white
          block below the image for maximum legibility (overlay-on-image
          looked muddy with a dark gradient layered over the cover). */}
      <section className="relative">
        {course.cover_image && (
          <div className="w-full max-h-[420px] overflow-hidden bg-slate-100">
            <img
              src={course.cover_image}
              alt=""
              className="w-full h-full object-cover max-h-[420px]"
            />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-12 lg:py-14">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
            {course.level}{course.duration ? ` · ${course.duration}` : ""}
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[1.02] text-slate-900">
            {course.title}
          </h1>
          {course.summary && (
            <p className="mt-5 text-base lg:text-lg text-slate-700 leading-relaxed max-w-3xl whitespace-pre-wrap">
              {course.summary}
            </p>
          )}
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 pb-16 space-y-8">
        {/* Progression info card */}
        {signedIn && (
          <div
            data-testid={COURSE_DETAIL.sequentialInfo}
            className="bg-white border border-blue-200 rounded-xl p-5 flex items-start gap-3"
          >
            <Info size={18} className="text-blue-800 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-slate-900">{t("courseDetailUi.sequentialInfoTitle")}</span>
              {t("courseDetailUi.sequentialInfoBody")}
            </div>
          </div>
        )}

        {course.chapters?.map((ch) => {
          const chapterTierLocked = (RANK[ch.tier] ?? 0) > userRank;
          const quizTierLocked = chapterTierLocked || (RANK[ch.quiz?.tier] ?? 0) > userRank;
          const prevChapterLocked = !ch.prev_chapter_passed && ch.number > 1;
          const chapterAccessible = !chapterTierLocked && !prevChapterLocked && signedIn;
          // Nudge — count premium-tier lessons visible-but-locked to the viewer.
          const premiumInChapter = (ch.lessons || []).filter(
            (l) => !l.free_preview && (RANK[l.tier] ?? 0) > userRank,
          ).length;
          return (
            <div
              key={ch.number}
              data-testid={COURSE_DETAIL.chapter}
              className={`bg-white border border-slate-200 rounded-2xl overflow-hidden ${prevChapterLocked ? "opacity-90" : ""}`}
            >
              <div className="p-6 sm:p-8 border-b border-slate-100 flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-2">
                    {t("courseDetailUi.chapterNumber", { num: String(ch.number).padStart(2, "0") })}
                  </div>
                  <h2 className="font-display font-bold text-2xl text-slate-900">
                    {ch.title}
                  </h2>
                  {ch.content_language && ch.content_language !== course.language && (
                    <div
                      data-testid="chapter-language-warning"
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-blue-800 bg-blue-50 border border-blue-200/70 rounded-full px-2.5 py-0.5"
                    >
                      <Languages size={11} />
                      {ch.content_language === "es" && t("courseDetailUi.recordedInSpanish")}
                      {ch.content_language === "en" && t("courseDetailUi.recordedInEnglish")}
                      {ch.content_language === "fr" && t("courseDetailUi.recordedInFrench")}
                    </div>
                  )}
                  {premiumInChapter > 0 && userTier !== "premium" && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-amber-700 bg-amber-50 border border-amber-200/70 rounded-full px-2.5 py-0.5">
                      <Sparkles size={11} />
                      {t("courseDetailUi.premiumLessonsLocked", {
                        count: premiumInChapter,
                      })}
                    </div>
                  )}
                  {prevChapterLocked && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      <Lock size={12} /> {t("courseDetailUi.chapterLocked", { n: ch.prev_chapter_number ?? ch.number - 1 })}
                    </div>
                  )}
                </div>
              </div>

              {/* Lessons */}
              <div>
                {ch.lessons.map((lesson, idx) => {
                  const tierLocked = !lesson.free_preview && (RANK[lesson.tier] ?? 0) > userRank;
                  const seqLocked = lesson.sequence_locked && signedIn;
                  const locked = tierLocked || seqLocked || prevChapterLocked;
                  return (
                    <div
                      key={lesson.id}
                      data-testid={COURSE_DETAIL.lessonRow}
                      className={`flex items-center gap-4 p-5 border-b border-slate-100 last:border-b-0 group transition-opacity ${
                        tierLocked ? "opacity-50 bg-slate-50/60" :
                        locked ? "opacity-70" : "hover:bg-blue-50/40"
                      }`}
                    >
                      <div className="w-10 shrink-0 flex items-center justify-center">
                        {tierLocked ? (
                          <Lock size={16} className="text-amber-600" />
                        ) : lesson.completed ? (
                          <CheckCircle2 size={18} className="text-blue-700" />
                        ) : (
                          <span className="text-slate-400 font-mono text-sm">
                            {ch.number}.{idx + 1}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className={`font-display font-semibold ${
                          tierLocked ? "text-slate-500" : "text-slate-900 group-hover:text-blue-800"
                        }`}>
                          {lesson.title}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <Clock size={12} />
                            {lesson.duration}
                          </span>
                          {lesson.attachments?.length > 0 && (
                            <span data-testid={`lesson-attachments-badge-${lesson.id}`} className="inline-flex items-center gap-1 text-slate-500" title="Attachments">
                              <Paperclip size={12} /> {lesson.attachments.length}
                            </span>
                          )}
                          {lesson.free_preview && (
                            <span
                              data-testid={COURSE_DETAIL.freePreviewBadge}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-widest text-[10px] font-semibold border border-amber-200"
                            >
                              <Sparkles size={10} /> {t("courseDetailUi.freePreview")}
                            </span>
                          )}
                          {tierLocked && (
                            <span
                              data-testid="lesson-premium-badge"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800 uppercase tracking-widest text-[10px] font-bold border border-amber-400/40"
                            >
                              <Sparkles size={10} /> {t("courseDetailUi.premiumOnly")}
                            </span>
                          )}
                          {lesson.completed && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 uppercase tracking-widest text-[10px] font-semibold border border-blue-100">
                              {t("courseDetailUi.completed")}
                            </span>
                          )}
                        </div>
                      </div>
                      {tierLocked ? (
                        <Link
                          to="/pricing"
                          data-testid={COURSE_DETAIL.lessonLock}
                          className="inline-flex items-center gap-1.5 text-xs text-amber-700 font-semibold hover:text-amber-600 transition-colors"
                        >
                          <Lock size={13} />
                          {t("courseDetailUi.upgradeCta")}
                        </Link>
                      ) : locked ? (
                        <div
                          data-testid={COURSE_DETAIL.lessonLock}
                          className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-medium"
                        >
                          <Lock size={14} />
                          {t("courseDetailUi.lessonLocked")}
                        </div>
                      ) : signedIn ? (
                        <Link to={`/lessons/${lesson.id}`} className="inline-flex items-center gap-1.5 text-blue-800 font-semibold text-sm">
                          <Play size={14} fill="currentColor" /> {t("courseDetailUi.watch")}
                        </Link>
                      ) : (
                        <Link to="/login" className="inline-flex items-center gap-1.5 text-blue-800 font-semibold text-sm">
                          {t("courseDetailUi.signIn")} <ChevronRight size={14} />
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Chapter quiz */}
              {ch.quiz && (
                <div className="p-6 sm:p-8 bg-blue-50/40 border-t border-blue-100">
                  <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                        ch.quiz.passed ? "bg-emerald-600 text-white" : "bg-blue-800 text-white"
                      }`}>
                        {ch.quiz.passed ? <CheckCircle2 size={20} /> : <ClipboardList size={20} />}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-600 font-semibold">
                          {t("courseDetailUi.chapterQuiz")}
                        </div>
                        <div className="font-display font-bold text-slate-900 text-lg">
                          {ch.quiz.title}
                          {ch.quiz.passed && (
                            <span className="ml-2 text-emerald-700 text-xs uppercase tracking-widest font-semibold">
                              {t("courseDetailUi.passed")}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {t("courseDetailUi.quizInfo", { count: ch.quiz.question_count, pass: ch.quiz.passing_score })}
                        </div>
                        {ch.quiz.sequence_locked && !prevChapterLocked && (
                          <div className="mt-2 text-xs text-amber-700">
                            {t("courseDetailUi.quizLockedMessage")}
                          </div>
                        )}
                      </div>
                    </div>
                    {chapterAccessible && !quizTierLocked && !ch.quiz.sequence_locked ? (
                      <Button
                        data-testid={COURSE_DETAIL.quizButton}
                        onClick={() => navigate(`/quizzes/${ch.quiz.id}`)}
                        className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6"
                      >
                        {ch.quiz.passed ? t("courseDetailUi.reviewQuiz") : t("courseDetailUi.takeQuiz")}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => navigate(signedIn ? "/pricing" : "/login")}
                        variant="outline"
                        disabled={signedIn && !quizTierLocked}
                        className="border-slate-300 h-11 px-6"
                      >
                        <Lock size={14} className="mr-2" />
                        {signedIn && !quizTierLocked
                          ? t("courseDetailUi.locked")
                          : (signedIn ? t("courseDetailUi.upgrade") : t("courseDetailUi.signIn"))}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Free-tier upsell strip */}
        {isFreeTier && freePreviews.length > 0 && (
          <div
            data-testid={COURSE_DETAIL.freeUpsell}
            className="relative overflow-hidden bg-slate-950 text-white rounded-2xl p-10 border border-amber-500/25"
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-3">
                  <Sparkles size={12} className="inline mr-1" />
                  {t("courseDetailUi.freePreviewCount", { count: freePreviews.length })}
                </div>
                <h3 className="font-display font-bold text-3xl tracking-tight">
                  {t("courseDetailUi.freeUpsellTitle")}
                </h3>
                <p className="mt-3 text-slate-300 max-w-xl text-sm leading-relaxed">
                  {t("courseDetailUi.freeUpsellBody")}
                </p>
              </div>
              <Link to="/pricing">
                <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 shrink-0">
                  {t("courseDetailUi.freeUpsellCta")}
                  <ChevronRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Certificate CTA */}
        {signedIn && certStatus && certStatus.chapter_count > 0 && (
          <div
            data-testid="course-certificate-section"
            className="bg-white border border-blue-200 rounded-2xl p-8 relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/10 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-800 text-white flex items-center justify-center shrink-0">
                  <Award size={22} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-amber-600 font-semibold">
                    {t("courseDetailUi.certificateEyebrow")}
                  </div>
                  <div className="mt-1 font-display font-bold text-slate-900 text-lg">
                    {certStatus.certificate
                      ? t("courseDetailUi.certificateIssued", {
                          date: new Date(certStatus.certificate.issued_at).toLocaleDateString(),
                          code: certStatus.certificate.code,
                        })
                      : certStatus.eligible
                        ? t("courseDetailUi.certificateReady")
                        : t("courseDetailUi.certificateProgress", {
                            done: certStatus.chapter_count - certStatus.missing_count,
                            total: certStatus.chapter_count,
                          })}
                  </div>
                </div>
              </div>
              {certStatus.certificate ? (
                <div className="flex flex-col items-end gap-2">
                  <Button
                    data-testid="course-certificate-view"
                    onClick={() => navigate(`/certificates/${certStatus.certificate.code}`)}
                    className={`h-11 px-6 text-white ${
                      certStatus.certificate.variant === "mastery"
                        ? "bg-amber-600 hover:bg-amber-500"
                        : "bg-blue-800 hover:bg-blue-700"
                    }`}
                  >
                    <Award size={16} className="mr-2" />
                    {certStatus.certificate.variant === "mastery"
                      ? t("courseDetail.certificate.viewMastery", "View Mastery cert")
                      : t("courseDetail.certificate.view")}
                  </Button>
                  {certStatus.can_upgrade_to_mastery && (
                    <Button
                      data-testid="course-certificate-upgrade"
                      onClick={claimCertificate}
                      disabled={issuing}
                      variant="outline"
                      className="border-amber-500 text-amber-800 hover:bg-amber-50 h-9 px-4 text-xs"
                    >
                      <Sparkles size={13} className="mr-1.5" />
                      {issuing
                        ? t("courseDetailUi.loading")
                        : t("courseDetailUi.upgradeToMastery")}
                    </Button>
                  )}
                </div>
              ) : certStatus.eligible ? (
                <div className="flex flex-col items-end gap-2">
                  <Button
                    data-testid="course-certificate-claim"
                    onClick={claimCertificate}
                    disabled={issuing}
                    className={`h-11 px-6 font-semibold ${
                      certStatus.mastery_eligible
                        ? "bg-amber-600 hover:bg-amber-500 text-white"
                        : "bg-amber-500 hover:bg-amber-400 text-slate-950"
                    }`}
                  >
                    <Award size={16} className="mr-2" />
                    {issuing
                      ? t("courseDetailUi.issuing")
                      : certStatus.mastery_eligible
                        ? t("courseDetailUi.claimMasteryCert")
                        : t("courseDetailUi.claimCertificate")}
                  </Button>
                  {!certStatus.mastery_eligible && certStatus.premium_lesson_count > 0 && (
                    <div className="text-[11px] text-amber-700 font-semibold text-right leading-snug max-w-[220px]">
                      {t("courseDetailUi.masteryProgress", {
                        done: certStatus.premium_completed_count,
                        total: certStatus.premium_lesson_count,
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {userTier === "basic" && certStatus?.eligible && <MissedPremiumRecap course={course} courseId={id} />}

        {(!user || userRank < (RANK[course.tier] ?? 0)) && !isFreeTier && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-wrap items-center gap-6 justify-between">
            <div>
              <div className="font-display font-bold text-slate-900 text-lg">
                {t("courseDetailUi.unlockCourseTitle")}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {t("courseDetailUi.unlockCourseBody", { tier: course.tier })}
              </div>
            </div>
            <Link to="/pricing">
              <Button className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6">
                {t("courseDetailUi.unlockCourseAction")}
              </Button>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
